/**
 * Turns a `ServicePredicate` (the services portion of an AI-produced drafts-search filter) into a short,
 * human-readable, localized summary string for display inside the services chip — e.g. `"Tax Prep", ≥100, monthly`.
 * Walks each service-leaf dimension (name, set predicates, decimal ranges, price-increase, nested `or` arms) and
 * joins the non-empty parts. `summarizeServicePredicate` is the public entry point used by the services chip.
 */ import { localizeBilledUpon, localizePricingType, localizeRecurrence, localizeStartOnType } from './services-localize';
import { currentValue } from './StringMatchChip/StringMatchChip.utils';
import { summarizeDecimalRange } from './summarizeDecimalRange';
const setSegment = (set, localize)=>{
    var _ref;
    const isExclude = set.nin != null && set.nin.length > 0;
    const values = (_ref = isExclude ? set.nin : set.in) !== null && _ref !== void 0 ? _ref : [];
    if (values.length === 0) return null;
    return "".concat(isExclude ? '≠' : '').concat(values.map(localize).join('/'));
};
const directFieldSegments = (predicate, t)=>{
    var _predicate_discount, _predicate_priceIncrease, _predicate_priceIncrease1, _predicate_priceIncrease2;
    const segments = [];
    const name = nameSegment(predicate.name);
    if (name != null) segments.push(name);
    const pushSet = (set, localize)=>{
        if (set == null) return;
        const segment = setSegment(set, localize);
        if (segment != null) segments.push(segment);
    };
    const pushDecimal = (range, label)=>{
        const bound = summarizeDecimalRange(range);
        segments.push(bound === '' ? label : "".concat(label, " ").concat(bound));
    };
    pushSet(predicate.billedUpon, (value)=>localizeBilledUpon(t, value));
    pushSet(predicate.recurrence, (value)=>localizeRecurrence(t, value));
    pushSet(predicate.pricingType, (value)=>localizePricingType(t, value));
    if (predicate.priceMin != null) pushDecimal(predicate.priceMin, t.servicesPriceMinLabel());
    if (predicate.priceMax != null) pushDecimal(predicate.priceMax, t.servicesPriceMaxLabel());
    if (predicate.cap != null) pushDecimal(predicate.cap, t.servicesCapLabel());
    if (((_predicate_discount = predicate.discount) === null || _predicate_discount === void 0 ? void 0 : _predicate_discount.amount) != null) pushDecimal(predicate.discount.amount, t.servicesDiscountAmountLabel());
    else if (predicate.discount != null) segments.push(t.servicesDiscountTypeLabel());
    if (((_predicate_priceIncrease = predicate.priceIncrease) === null || _predicate_priceIncrease === void 0 ? void 0 : _predicate_priceIncrease.percent) != null) {
        pushDecimal(predicate.priceIncrease.percent, t.servicesPriceIncreasePercentLabel());
    } else if (((_predicate_priceIncrease1 = predicate.priceIncrease) === null || _predicate_priceIncrease1 === void 0 ? void 0 : _predicate_priceIncrease1.exists) === false) {
        segments.push(t.servicesPriceIncreaseNoneLabel());
    } else if (predicate.priceIncrease != null && predicate.priceIncrease.startOnType == null) {
        segments.push(t.servicesPriceIncreaseExistsLabel());
    }
    const startOnType = (_predicate_priceIncrease2 = predicate.priceIncrease) === null || _predicate_priceIncrease2 === void 0 ? void 0 : _predicate_priceIncrease2.startOnType;
    if (startOnType != null) {
        const segment = setSegment(startOnType, (value)=>localizeStartOnType(t, value));
        if (segment != null) segments.push("".concat(t.servicesPriceIncreaseStartOnLabel(), " ").concat(segment));
    }
    return segments;
};
const orGroupSegment = (arms, t)=>{
    const armSummaries = arms.map((arm)=>summarizeServiceConditions(arm, t)).filter((summary)=>summary !== '');
    if (armSummaries.length === 0) return null;
    return armSummaries.join(" ".concat(t.orConnector(), " "));
};
export const nameSegment = (name)=>{
    if (name == null) return null;
    const value = currentValue(name);
    return value.trim() === '' ? null : '"'.concat(value, '"');
};
export const summarizeServiceConditions = (predicate, t)=>{
    const segments = directFieldSegments(predicate, t);
    if (predicate.or != null && predicate.or.length > 0) {
        const orSegment = orGroupSegment(predicate.or, t);
        if (orSegment != null) segments.push(orSegment);
    }
    return segments.join(', ');
};
export const summarizeServicePredicate = (predicate, t)=>{
    const conditions = summarizeServiceConditions(predicate, t);
    const base = conditions !== '' ? conditions : t.servicesLabel();
    return predicate.not != null ? "".concat(base, " …") : base;
};
